import React, { useState } from "react";
import { Gift } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import GiftSheet from "./GiftSheet";

/**
 * GiftButton — a generic "Send a gift" button that opens the GiftSheet
 * pre-bound to a recipient + target.
 *
 * Props:
 *   recipient   — { user_id, name, picture? } (REQUIRED)
 *   targetKind  — defaults to "profile"
 *   targetId    — defaults to recipient.user_id
 *   variant     — "icon" (small icon button) | "compact" (icon + count) | "pill" (full pill button)
 *   count       — number to show next to the icon (e.g. total gifts received on this post)
 *   className   — extra wrapper classes
 *   testIdPrefix — defaults to "gift-btn"
 */
export default function GiftButton({
  recipient,
  targetKind = "profile",
  targetId,
  variant = "icon",
  count = 0,
  className = "",
  testIdPrefix = "gift-btn",
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handle = () => {
    if (!user) {
      navigate("/login");
      return;
    }
    if (recipient?.user_id === user.user_id) return; // silently no-op on self
    setOpen(true);
  };

  // Hide for self
  if (user && recipient?.user_id === user.user_id) return null;

  if (variant === "pill") {
    return (
      <>
        <button
          onClick={handle}
          className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-gradient-to-r from-pink-500/15 to-cyan-500/15 hover:from-pink-500/25 hover:to-cyan-500/25 border border-pink-400/30 text-pink-200 text-xs font-bold ${className}`}
          data-testid={`${testIdPrefix}-pill`}
        >
          <Gift className="w-4 h-4" /> Send a gift
        </button>
        <GiftSheet
          open={open}
          onOpenChange={setOpen}
          recipient={recipient}
          targetKind={targetKind}
          targetId={targetId || recipient?.user_id}
        />
      </>
    );
  }

  // compact + icon
  return (
    <>
      <button
        onClick={handle}
        aria-label="Send a gift"
        title="Send a gift"
        className={`inline-flex flex-col items-center gap-1 text-zinc-300 hover:text-pink-300 transition-colors ${className}`}
        data-testid={`${testIdPrefix}-icon`}
      >
        <Gift className="w-6 h-6" />
        {variant === "compact" && (
          <span className="text-[10px] font-bold leading-none">
            {count > 0 ? count : "Gift"}
          </span>
        )}
      </button>
      <GiftSheet
        open={open}
        onOpenChange={setOpen}
        recipient={recipient}
        targetKind={targetKind}
        targetId={targetId || recipient?.user_id}
      />
    </>
  );
}
